/**
 * Persistencia de vistas personalizadas por lista (genérico).
 * Clave: smarty:list-views:{listId}:{tenantId}
 * Usado por listas con vistas personalizadas (Categorías, Etiquetas, etc.). Usuarios usa useUsersViews + usersStorage para vistas; preferencias de columnas en listColumnPreferences (listId 'users').
 */

export interface ListViewGeneric {
  id: string;
  label: string;
  isSystem: boolean;
  query: string;
  filterValues: Record<string, string[]>;
  /** Niveles de agrupación (Group By estilo Odoo). Opcional: listas sin agrupación lo omiten. */
  groupBy?: string[];
  /** Si true, esta vista se carga automáticamente al entrar a la lista (filtro por defecto). */
  isDefault?: boolean;
}

const STORAGE_PREFIX = 'smarty:list-views';

function storageKey(listId: string, tenantId: string | undefined): string {
  return tenantId ? `${STORAGE_PREFIX}:${listId}:${tenantId}` : '';
}

function isListViewGeneric(obj: unknown): obj is ListViewGeneric {
  if (obj == null || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.label === 'string' &&
    typeof o.isSystem === 'boolean' &&
    typeof o.query === 'string' &&
    o.filterValues != null &&
    typeof o.filterValues === 'object' &&
    !Array.isArray(o.filterValues) &&
    // Opcionales: si vienen, deben tener el tipo correcto (ausentes = ok, retrocompatible).
    (o.groupBy === undefined || Array.isArray(o.groupBy)) &&
    (o.isDefault === undefined || typeof o.isDefault === 'boolean')
  );
}

export function loadListCustomViews(
  listId: string,
  tenantId: string | undefined
): ListViewGeneric[] {
  const key = storageKey(listId, tenantId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isListViewGeneric) : [];
  } catch {
    return [];
  }
}

export function saveListCustomViews(
  listId: string,
  tenantId: string | undefined,
  custom: ListViewGeneric[]
): void {
  const key = storageKey(listId, tenantId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(custom));
  } catch {
    /* ignore */
  }
}
